import {
  Button,
  Drawer,
  HStack,
  Input,
  Portal,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import type { Demand, InterestedSib } from '../types'

interface AssignDemandDrawerProps {
  open: boolean
  demand: Demand
  sibs: InterestedSib[]
  isSubmitting?: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (
    assignments: Array<{
      sibId: string
      serviceType: string
      units: number
    }>
  ) => void
}

const SERVICE_TYPES = [
  { key: 'TOJ', label: 'TOJ', unit: 'TOJ' },
  { key: 'CONSULTING', label: 'Beratung', unit: 'Std.' },
  { key: 'TRAINING', label: 'Training', unit: 'Tr.' },
  { key: 'PILOT', label: 'Pilot', unit: 'Stk.' },
]

function getDemandUnits(demand: Demand, serviceType: string) {
  return demand.buckets?.find(bucket => bucket.serviceType === serviceType)?.requestedUnits ?? 0
}

export function AssignDemandDrawer({
  open,
  demand,
  sibs,
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: AssignDemandDrawerProps) {
  const [values, setValues] = useState<Record<string, string>>({})

  const selectedSib = sibs[0]

  const canSubmit = useMemo(() => Object.values(values).some(value => Number(value) > 0), [values])

  function fieldKey(sibId: string, serviceType: string) {
    return `${sibId}:${serviceType}`
  }

  function handleSubmit() {
    if (!selectedSib || !canSubmit) return

    const assignments = SERVICE_TYPES.map(service => ({
      sibId: selectedSib.id,
      serviceType: service.key,
      units: Number(values[fieldKey(selectedSib.id, service.key)] || 0),
    })).filter(item => item.units > 0)

    onSubmit(assignments)
  }

  return (
    <Drawer.Root open={open} onOpenChange={details => onOpenChange(details.open)} size="xl">
      <Portal>
        <Drawer.Backdrop />

        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header>
              <HStack justify="space-between" w="full">
                <Stack gap={1}>
                  <Drawer.Title>Zuschlag geben</Drawer.Title>
                  <Drawer.Description>
                    Verteile den Bedarf auf die ausgewählten SIB.
                  </Drawer.Description>
                </Stack>

                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Abbrechen
                </Button>
              </HStack>
            </Drawer.Header>

            <Drawer.Body>
              <Stack gap={6}>
                <Stack borderWidth="1px" borderColor="border.muted" borderRadius="lg" p={6}>
                  <Text fontWeight="semibold">Offener Bedarf</Text>

                  <SimpleGrid columns={4} gap={6}>
                    {SERVICE_TYPES.map(service => (
                      <Stack key={service.key} gap={0}>
                        <Text color="fg.muted" fontSize="sm">
                          {service.label}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold">
                          {getDemandUnits(demand, service.key)}
                        </Text>
                        <Text color="fg.muted" fontSize="sm">
                          {service.unit}
                        </Text>
                      </Stack>
                    ))}
                  </SimpleGrid>
                </Stack>

                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Leistung</Table.ColumnHeader>
                      <Table.ColumnHeader>{selectedSib?.name ?? 'SIB'}</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {SERVICE_TYPES.map(service => {
                      const max = getDemandUnits(demand, service.key)
                      const key = selectedSib ? fieldKey(selectedSib.id, service.key) : service.key

                      return (
                        <Table.Row key={service.key}>
                          <Table.Cell>{service.label}</Table.Cell>

                          <Table.Cell>
                            <HStack>
                              <Input
                                type="number"
                                min={0}
                                max={max}
                                value={values[key] ?? ''}
                                onChange={event =>
                                  setValues(current => ({
                                    ...current,
                                    [key]: event.target.value,
                                  }))
                                }
                              />

                              <Text
                                bg="bg.muted"
                                borderRadius="md"
                                px={2}
                                py={1}
                                whiteSpace="nowrap"
                                fontSize="sm"
                              >
                                Rest {Math.max(max - Number(values[key] || 0), 0)}
                              </Text>
                            </HStack>
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Root>
              </Stack>
            </Drawer.Body>

            <Drawer.Footer>
              <HStack>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Abbrechen
                </Button>

                <Button
                  colorPalette="gray"
                  disabled={!canSubmit}
                  loading={isSubmitting}
                  onClick={handleSubmit}
                >
                  Zuschlag geben
                </Button>
              </HStack>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
